import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import { api } from '../services/api';
import BankExpensesPanel from '../components/BankExpensesPanel';
import MoneyToday from '../components/MoneyToday';
import SpendingMonth from '../components/SpendingMonth';
import { Download, Plus, X, ChevronRight, Search, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { businessToday } from '../utils/time';
import { useReadOnly } from '../hooks/useReadOnly';
import { invalidateMoney } from '../utils/moneyQueries';

/**
 * The profit page, reworked (owner, Sep 28 2026: "fully rework the profit
 * page to a usable page"). Same engine as before — every number still comes
 * from server/services/jobProfit.js, so this page, the CSV and the dashboard
 * cannot disagree — but the reading changed:
 *
 *   - One money stack: charged → crew pay → job costs → before overhead →
 *     overhead → kept. The last number is the one that matters, and on a
 *     single month it includes overhead instead of hiding it in a side box.
 *   - Three views. By day (how a route day did), by customer (who makes and
 *     who loses money), by job (the detail, as cards, not a 15-column table).
 *   - Data gaps are a tap ("Needs a look"), not a banner to scroll past.
 *
 * The old spreadsheet lives on as the CSV export, unchanged.
 */

const JOB_CATEGORIES = [
  { value: 'material',      label: 'Material',      hint: 'Went into the yard and stayed there' },
  { value: 'supplies_used', label: 'Supplies used', hint: 'The part of your stock this job used up' },
];
/** Categories older rows can still carry. Offered only when editing such a row, so an edit never flips it. */
const LEGACY_CATEGORY_LABELS: Record<string, string> = {
  supplies: 'Supplies (old)', rental: 'Rental (old)', extra_labor: 'Extra labour (old)',
};

function money(n: number | null | undefined) {
  const v = Number(n) || 0;
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
const hrs = (n: number | null | undefined) => (Number(n) || 0).toFixed(1);
const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const noon = (day: string) => new Date(`${day}T12:00:00`);
const dayLabel = (day: string) => noon(day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const shortDay = (day: string) => noon(day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const monthLabel = (ym: string) => noon(`${ym}-02`).toLocaleDateString('en-US', { month: 'long' });

type Preset = 'mtd' | 'last_month' | 'ytd' | 'all' | 'custom';

/**
 * Presets cover the whole calendar month / year, not "up to today": nothing
 * finished is dated after today, so Work done is unchanged, and Everything /
 * Booked ahead can then see the rest of the period's booked visits.
 */
function presetRange(p: Preset): { start: string; end: string } {
  const t = businessToday();
  const d = noon(t);
  const y = d.getFullYear(), m = d.getMonth();
  switch (p) {
    case 'mtd':        return { start: iso(new Date(y, m, 1)), end: iso(new Date(y, m + 1, 0)) };
    case 'last_month': return { start: iso(new Date(y, m - 1, 1)), end: iso(new Date(y, m, 0)) };
    case 'ytd':        return { start: `${y}-01-01`, end: `${y}-12-31` };
    default:           return { start: '', end: '' };
  }
}

/** Both ends in the same calendar month (the month), or null. */
function sameMonth(range: { start: string; end: string }): string | null {
  if (!range.start || !range.end) return null;
  const ym = range.start.slice(0, 7);
  return range.end.slice(0, 7) === ym ? ym : null;
}

/**
 * The one month a range IS: it starts on the 1st and runs to the month's last
 * day (or to today, in the running month). Sep 10 → Sep 15 is not September,
 * so it gets the rows' own stack, not the whole month's.
 */
function singleMonth(range: { start: string; end: string }): string | null {
  const ym = sameMonth(range);
  if (!ym || range.start !== `${ym}-01`) return null;
  const last = iso(new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)), 0));
  const today = businessToday();
  return range.end === last || (ym === today.slice(0, 7) && range.end === today) ? ym : null;
}

/**
 * A row the guesswork banner counted. The server sends `is_gap` / `gaps`;
 * until it does, the same test the server's stats use, from the structured
 * fields — never "has any note", since drive time, plan shares and add-ons are
 * notes too, and they are facts, not gaps.
 */
function isGapRow(r: any): boolean {
  if (typeof r.is_gap === 'boolean') return r.is_gap;
  if (Array.isArray(r.gaps)) return r.gaps.length > 0;
  const notes: string[] = r.notes || [];
  return r.price_source === 'missing' || r.pay_source === 'missing' || r.hours_source === 'actual_derived'
    || notes.includes('no crew assigned') || notes.includes('no route');
}

const STATUS_CHIP: Record<string, string> = {
  complete: 'bg-green-100 text-green-800', scheduled: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-800', skipped: 'bg-amber-100 text-amber-800',
};

export default function Profit() {
  const readOnly = useReadOnly();
  const [preset, setPreset] = useState<Preset>('mtd');
  const [range, setRange] = useState(() => presetRange('mtd'));
  const [status, setStatus] = useState('complete');
  const [view, setView] = useState<'days' | 'customers' | 'jobs'>('days');
  const [gapsOnly, setGapsOnly] = useState(false);
  const [q, setQ] = useState('');
  const [expenseFor, setExpenseFor] = useState<any | null>(null);
  const [exporting, setExporting] = useState(false);

  // A new question clears the gaps filter — otherwise it sticks on with its only toggle gone.
  useEffect(() => { setGapsOnly(false); }, [range.start, range.end, status]);

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    const today = businessToday();
    // Booked ahead means from today on: a past-dated 'scheduled' row is a missed visit, not income.
    // Only in the /profit + CSV params — the bank list and the stack's month still read `range`.
    const start = status === 'scheduled' && preset !== 'custom' && (!range.start || range.start < today) ? today : range.start;
    if (start) p.start = start;
    if (range.end) p.end = range.end;
    if (status) p.status = status;
    return p;
  }, [range, status, preset]);

  const { data, isLoading, error, isPreviousData } = useQuery(['profit', params],
    () => api.get('/profit', { params }).then(r => r.data), { keepPreviousData: true });

  // /profit#bank-expenses (Needs Attention's "Review expenses") — React Router does not scroll to hashes.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash || isLoading) return;
    const t = setTimeout(() => document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    return () => clearTimeout(t);
  }, [hash, isLoading]);

  const rows: any[] = useMemo(() => {
    let all: any[] = data?.rows || [];
    if (gapsOnly) all = all.filter(isGapRow);
    const needle = q.trim().toLowerCase();
    if (needle) all = all.filter(r =>
      String(r.job_name || '').toLowerCase().includes(needle)
      || String(r.customer_name || '').toLowerCase().includes(needle)
      || String(r.route_name || '').toLowerCase().includes(needle));
    return all;
  }, [data, gapsOnly, q]);

  const totals = data?.totals;
  const stats = data?.stats;
  const filtered = gapsOnly || !!q.trim();
  const month = singleMonth(range);
  const spendMonth = month || sameMonth(range);
  // Under "Everything" the month stack is the ledger (finished work); the booked visits are shown
  // beside it, from the unfiltered rows the cards list.
  const booked = useMemo(() => (status === '' ? sum((data?.rows || []).filter((r: any) => r.status === 'scheduled')) : null), [data, status]);

  // What the guesswork is, in words — every count the server's gap test uses, so the list explains the number.
  const gapWords = stats ? [stats.noPrice > 0 && `${stats.noPrice} no price`, stats.noCrew > 0 && `${stats.noCrew} no crew`,
    stats.noRate > 0 && `${stats.noRate} no pay rate on file`, stats.payNotSet > 0 && `${stats.payNotSet} pay not set`,
    stats.derived > 0 && `${stats.derived} time from the estimate`, stats.noMinutes > 0 && `${stats.noMinutes} no time at all`,
    stats.noRoute > 0 && `${stats.noRoute} no route`]
    .filter(Boolean).join(' · ') : '';

  const periodWords = preset === 'all' && !params.start ? 'All time'
    : `${params.start ? shortDay(params.start) : 'the start'} → ${params.end ? shortDay(params.end) : 'now'}`;
  const bookedFromPast = status === 'scheduled' && !!params.start && !!params.end && params.start > params.end;

  function applyPreset(p: Preset) {
    setPreset(p);
    if (p !== 'custom') setRange(presetRange(p));
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const res = await api.get('/profit/export-csv', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      const tag = status === 'complete' ? 'work-done' : status === 'scheduled' ? 'booked-ahead' : 'everything';
      a.download = `job-profit-${tag}-${params.start || 'all'}${params.end ? `_to_${params.end}` : ''}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); } finally { setExporting(false); }
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Profit</h1>
          <p className="text-sm text-gray-500 mt-0.5">Finished work. The big number is after the $750 you take home.</p>
        </div>
        <button onClick={exportCsv} disabled={exporting || !(data?.rows?.length)}
          title={filtered ? 'The CSV is the whole period — the search and the guesswork filter are not applied' : undefined}
          className="press flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-black text-sm font-medium px-4 py-2 rounded-xl">
          <Download className="h-4 w-4" />{exporting ? 'Exporting…' : filtered ? 'CSV (whole period)' : 'CSV'}
        </button>
      </div>

      {/* When and what counts */}
      <div className="card p-3 space-y-2">
        <div className="flex flex-wrap gap-1">
          {([['mtd', 'This month'], ['last_month', 'Last month'], ['ytd', 'This year'], ['all', 'All time'], ['custom', 'Pick dates']] as [Preset, string][]).map(([p, label]) => (
            <button key={p} onClick={() => applyPreset(p)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${preset === p ? 'bg-brand-50 border-brand-400 text-brand-800' : 'border-gray-200 text-gray-600'}`}>
              {label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="flex gap-2">
            <input type="date" value={range.start} onChange={e => setRange(r => ({ ...r, start: e.target.value }))} className="input py-1.5 text-sm" />
            <input type="date" value={range.end} onChange={e => setRange(r => ({ ...r, end: e.target.value }))} className="input py-1.5 text-sm" />
          </div>
        )}
        <div className="flex flex-wrap gap-1">
          {([['complete', 'Work done'], ['', 'Everything'], ['scheduled', 'Booked ahead']] as [string, string][]).map(([s, label]) => (
            <button key={s} onClick={() => setStatus(s)}
              className={`text-xs px-3 py-1.5 rounded-lg border ${status === s ? 'bg-gray-900 border-gray-900 text-white' : 'border-gray-200 text-gray-600'}`}>
              {label}
            </button>
          ))}
          <span className="text-[11px] text-gray-500 self-center ml-1">
            {status === 'complete' ? 'Finished visits only — money that is real.'
              : status === 'scheduled' ? (preset === 'custom' ? 'What the booked work in these dates should make.' : 'What the work booked from today on should make.')
              : 'Finished and booked together.'}
          </span>
        </div>
        {bookedFromPast && (
          <p className="text-[11px] text-amber-800">Booked ahead starts today, and this period is over — nothing is booked in it. Pick This month, This year or All time.</p>
        )}
      </div>

      {/* How much money do I have — this morning (owner, Sep 28 2026). */}
      <MoneyToday compact />

      {/* The money story, top to bottom. One whole calendar month of finished work reads the ledger —
          plans, contracts, the pay ledger, every job cost, overhead — from services/money. Finished work
          over any other range reads the statement (the rows plus each month's overhead and the $750 draw).
          Booked work reads the job sheet, with no overhead taken off. */}
      {status !== 'scheduled' && month
        ? <MonthStack month={month} booked={booked} bookedStale={isPreviousData} />
        : status !== 'scheduled' && data?.statement
          ? <RangeBooks statement={data.statement} heading={!params.end || params.end > businessToday() ? `${periodWords} · finished through today` : periodWords} booked={booked} stale={isPreviousData} />
          : totals && (
            <MoneyStack totals={totals} status={status} stale={isPreviousData}
              heading={status === 'scheduled' ? `${periodWords} · booked, not done yet` : periodWords} />
          )}

      {/* Data gaps: one line, tap to see the rows */}
      {stats && (stats.withGaps > 0 || gapsOnly) && (
        <button onClick={() => { setGapsOnly(g => !g); if (!gapsOnly) setView('jobs'); }}
          className={`w-full text-left rounded-xl border px-4 py-2.5 text-sm ${gapsOnly ? 'bg-amber-100 border-amber-400 text-amber-900' : 'bg-amber-50 border-amber-200 text-amber-800'} ${isPreviousData ? 'opacity-50' : ''}`}>
          {stats.withGaps > 0 ? (
            <>
              <span className="font-semibold">{stats.withGaps} of {totals?.jobs} jobs are part guesswork</span>
              {gapWords ? ` — ${gapWords}.` : '.'}
              <span className="font-semibold"> {gapsOnly ? 'Showing them — tap to show all.' : 'Tap to see them.'}</span>
            </>
          ) : (
            <span className="font-semibold">No gaps in this period — tap to show all.</span>
          )}
        </button>
      )}

      {/* Views */}
      <div className="flex items-center gap-2">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          {([['days', 'By day'], ['customers', 'By customer'], ['jobs', 'Every job']] as const).map(([v, label]) => (
            <button key={v} onClick={() => setView(v)}
              className={`text-xs font-semibold px-3 py-2 ${view === v ? 'bg-gray-900 text-white' : 'text-gray-600'}`}>
              {label}
            </button>
          ))}
        </div>
        <label className="relative flex-1 min-w-0">
          <Search className="h-3.5 w-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Find a customer, route…"
            className="input w-full py-1.5 pl-8 text-sm" />
        </label>
      </div>

      <div className={isPreviousData ? 'opacity-50 pointer-events-none' : ''} aria-busy={isPreviousData}>
        {isLoading ? (
          <div className="card py-16 text-center text-gray-500 text-sm">Loading…</div>
        ) : error ? (
          <div className="card py-16 text-center text-red-600 text-sm">Could not load the numbers.</div>
        ) : rows.length === 0 ? (
          <div className="card py-16 text-center text-gray-500 text-sm">{filtered ? 'Nothing matches.' : 'No jobs in this period.'}</div>
        ) : view === 'days' ? (
          <DayView rows={rows} onCost={setExpenseFor} readOnly={readOnly} />
        ) : view === 'customers' ? (
          <CustomerView rows={rows} onCost={setExpenseFor} readOnly={readOnly} />
        ) : (
          <div className="space-y-2">
            {rows.map(r => <JobCard key={r.job_id} r={r} onCost={setExpenseFor} readOnly={readOnly} />)}
          </div>
        )}
      </div>

      {filtered && totals && (
        <p className="text-xs text-gray-500">
          The lists are filtered; the money stack above covers {status !== 'scheduled' && month ? `the whole of ${monthLabel(month)}` : periodWords}, unfiltered.
        </p>
      )}

      {/* Every dollar out this month (owner, Sep 28 2026: "track all spending"). Spending is read one
          calendar month at a time, so it only shows when the period sits inside one month. */}
      {spendMonth ? (
        <SpendingMonth month={spendMonth} wholeMonth={!month}
          onSort={() => document.getElementById('bank-expenses')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
      ) : (
        <div className="card p-4 text-sm text-gray-500">
          Spending is shown one month at a time. Pick This month, Last month, or dates inside one month to see it.
        </div>
      )}

      {/* Bank purchases → job costs (the panel carries id="bank-expenses") */}
      <BankExpensesPanel range={range} />

      {expenseFor && <ExpenseModal job={expenseFor} readOnly={readOnly} onClose={() => setExpenseFor(null)} />}
    </div>
  );
}

/**
 * Finished work over any range that is not one whole month (routes/profit.js
 * `statement`): the rows' money, each covered month's overhead, and the $750
 * a month the owner takes home. The big number is after that draw.
 */
function RangeBooks({ statement, heading, booked, stale }: { statement: any; heading: string; booked: { charged: number; pay: number; margin: number } | null; stale: boolean }) {
  const s = statement;
  const lines: { label: string; value: number; hide?: boolean; strong?: boolean }[] = [
    { label: 'Work finished', value: s.revenue },
    { label: 'Crew', value: s.crew },
    { label: 'Materials on the jobs', value: s.materials, hide: !s.materials },
    { label: 'Subs', value: s.subs, hide: !s.subs },
    { label: 'Gas', value: s.gas, hide: !s.gas },
    { label: 'Insurance, software, storage', value: s.fixed, hide: !s.fixed },
    { label: 'Parts and oil', value: s.parts, hide: !s.parts },
    { label: 'Machines wearing out', value: s.machines, hide: !s.machines },
    { label: 'Getting started, spread out', value: s.startup, hide: !s.startup },
  ];
  return (
    <div className={`card p-4 space-y-3 ${stale ? 'opacity-50' : ''}`} aria-busy={stale}>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{heading} · what's left{stale ? ' · updating…' : ''}</p>
        <p className={`text-3xl font-black ${s.left >= 0 ? 'text-green-700' : 'text-red-600'}`}>{money(s.left)}</p>
        <p className="text-sm text-gray-600 mt-1">
          After {money(s.draw_per_month)} a month × {s.months} months. {s.jobs} finished job{s.jobs === 1 ? '' : 's'}. Scheduled work is not in this.
        </p>
      </div>
      <div className="border-t border-gray-100 pt-2 space-y-1.5">
        {lines.filter(r => !r.hide).map(r => (
          <div key={r.label} className="flex justify-between gap-3 text-sm">
            <span className="text-gray-700">{r.label}</span>
            <span className="font-semibold tabular-nums">{r.label === 'Work finished' ? money(r.value) : `−${money(r.value)}`}</span>
          </div>
        ))}
        <div className="flex justify-between gap-3 text-sm font-semibold pt-1 border-t border-gray-200">
          <span>Left before you</span>
          <span className="tabular-nums">{money(s.before_you)}</span>
        </div>
        <div className="flex justify-between gap-3 text-sm">
          <span className="text-gray-700">You take home</span>
          <span className="font-semibold tabular-nums">−{money(s.draw)}</span>
        </div>
      </div>
      {booked && (
        <div className="flex justify-between gap-3 text-sm border-t border-gray-100 pt-2">
          <span className="text-gray-700">Booked, not done yet
            <span className="block text-[11px] text-gray-500">crew pay {money(booked.pay)} · {money(booked.margin)} before overhead — not in the numbers above</span>
          </span>
          <span className="font-semibold tabular-nums">{money(booked.charged)}</span>
        </div>
      )}
      <LaborerCompare before={s.before_you} hours={Number(s.your_hour_count) || 0} rate={Number(s.laborer_rate) || 0} />
      <p className="text-xs text-gray-500">
        You pay yourself {money(s.draw_per_month)} a month. That is money you took home, not a cost. Left before you is what the business made.
      </p>
    </div>
  );
}

/**
 * The owner's question (Oct 6 2026): "include a price for if I was actually a
 * laborer vs I'm not so I can see real differences." Two readings of the same
 * numbers: he works unpaid (what the business made), or his hours cost what a
 * laborer at his Employees rate would.
 */
function LaborerCompare({ before, hours, rate, approx = false }: { before: number; hours: number | null; rate: number; approx?: boolean }) {
  const cost = hours != null ? Math.round(hours * rate * 100) / 100 : null;
  const after = cost != null ? Math.round((before - cost) * 100) / 100 : null;
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1.5 text-sm">
      <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">You working vs. paying a laborer</p>
      <div className="flex justify-between gap-3"><span className="text-gray-700">You do it (no wage for you)</span><span className={`font-semibold tabular-nums ${before >= 0 ? 'text-green-700' : 'text-red-600'}`}>{money(before)}</span></div>
      {cost != null && after != null ? (
        <>
          <div className="flex justify-between gap-3"><span className="text-gray-700">A laborer for your {approx ? 'about ' : ''}{(hours as number).toFixed(1)} hours at {money(rate)}/h</span><span className="font-semibold tabular-nums">−{money(cost)}</span></div>
          <div className="flex justify-between gap-3 pt-1 border-t border-gray-200"><span className="font-semibold text-gray-900">If you paid a laborer</span><span className={`font-bold tabular-nums ${after >= 0 ? 'text-green-700' : 'text-red-600'}`}>{money(after)}</span></div>
        </>
      ) : <p className="text-[11px] text-gray-500">Your hours are not known for this month, so there is nothing to price.</p>}
    </div>
  );
}

/**
 * Charged → crew pay → job costs → before overhead, from the job sheet: booked
 * work, or finished work when the statement could not be built. Overhead is
 * not taken off here; overhead off work that has not happened would be a lie.
 */
function MoneyStack({ totals, status, heading, stale }: { totals: any; status: string; heading: string; stale: boolean }) {
  // Whatever the engine subtracted from charged besides pay: materials, supplies, rentals, subs.
  const costs = Math.round((totals.charged - totals.total_pay - totals.margin) * 100) / 100;
  const n = (v: any) => Number(v) || 0;
  // Subcontractor cost is in the margin but not (yet) in the totals — the rest of the costs is it.
  const subs = totals.subcontractor != null ? n(totals.subcontractor)
    : Math.round((costs - n(totals.material) - n(totals.supplies) - n(totals.rental) - n(totals.extra_labor)) * 100) / 100;
  const costParts = [
    n(totals.material) > 0 && `materials ${money(totals.material)}`,
    n(totals.supplies) > 0 && `supplies ${money(totals.supplies)}`,
    n(totals.rental) > 0 && `rentals ${money(totals.rental)}`,
    n(totals.extra_labor) > 0 && `extra labour ${money(totals.extra_labor)}`,
    subs > 0.005 && `subs ${money(subs)}`,
  ].filter(Boolean).join(' · ');
  const unattached = totals.unattached;

  const Line = ({ label, value, sub, sign, strong, tone }: { label: string; value: number; sub?: string; sign?: '-' | '='; strong?: boolean; tone?: string }) => (
    <div className={`flex items-baseline justify-between gap-3 ${strong ? 'pt-2 border-t border-gray-200' : ''}`}>
      <div className="min-w-0">
        <span className={`${strong ? 'font-bold text-gray-900' : 'text-gray-700'} text-sm`}>{sign ? `${sign} ` : ''}{label}</span>
        {sub && <span className="block text-[11px] text-gray-500">{sub}</span>}
      </div>
      <span className={`${strong ? 'text-xl font-bold' : 'text-sm font-semibold'} whitespace-nowrap ${tone || 'text-gray-900'}`}>{money(value)}</span>
    </div>
  );

  return (
    <div className={`card p-4 space-y-2 ${stale ? 'opacity-50' : ''}`} aria-busy={stale}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{heading}{stale ? ' · updating…' : ''}</p>
      <Line label={status === 'scheduled' ? 'To be charged' : status === '' ? 'Charged and booked' : 'Charged'} value={totals.charged}
        sub={`${totals.jobs} job${totals.jobs === 1 ? '' : 's'}${totals.complete !== totals.jobs ? ` · ${totals.complete} done` : ''}`} />
      <Line label="Crew pay" value={totals.total_pay} sign="-"
        sub={`me ${money(totals.my_pay)} · crew ${money(totals.employee_pay)} · ${hrs(totals.man_hours)} man-hours`} />
      <Line label="Job costs" value={costs} sign="-"
        sub={Math.abs(costs) > 0.005 ? costParts || undefined : 'none entered — add them on the jobs below'} />
      <Line label={status === 'scheduled' ? 'Would be left before overhead' : 'Before overhead'} value={totals.margin} sign="=" strong
        tone={totals.margin >= 0 ? 'text-green-700' : 'text-red-600'}
        sub={totals.margin_pct != null ? `${totals.margin_pct.toFixed(0)}% of charged` : undefined} />
      {unattached && (n(unattached.pay) || n(unattached.plan_bills) || n(unattached.contract_bills)) ? (
        <p className="text-[11px] text-gray-500">
          Includes {money(unattached.pay)} of pay and {money(n(unattached.plan_bills) + n(unattached.contract_bills))} of bills not tied to a visit.
        </p>
      ) : null}
      <p className="text-[11px] text-gray-500">
        {status === 'scheduled'
          ? 'Overhead is not taken off booked work. Work done on one whole month shows profit after it.'
          : <>Overhead is a monthly number — pick one whole month to see profit after it.
            {!unattached ? ' Pay not tied to a visit (bonuses, commissions) and plan or contract bills in a month with no visit are only in the one-month stack.' : ''}</>}
      </p>
    </div>
  );
}

/**
 * One calendar month, whole (services/money): what was earned including the
 * plan and contract bills, the pay LEDGER rather than a recomputation, every
 * job cost, overhead cash plus the month's share of the machines — and how
 * much of it is real money. It is finished work only (booked visits have no
 * ledger yet); under "Everything" the booked visits get their own line from
 * the job rows. The gaps banner and the views below come from the job sheet.
 */
function MonthStack({ month, booked, bookedStale }: { month: string; booked: { charged: number; pay: number; margin: number } | null; bookedStale: boolean }) {
  const { data, isLoading, isError, error, refetch, isPreviousData } = useQuery(['money-month', month], () => api.get('/money/month', { params: { month } }).then(r => r.data), { keepPreviousData: true, retry: 1 });
  if (isError) return (
    <div className="card p-4 text-sm text-amber-800 bg-amber-50 border border-amber-200">
      Could not add up the month{(error as any)?.response?.data?.message ? ` — ${(error as any).response.data.message}` : ''}.
      <button type="button" onClick={() => refetch()} className="ml-2 underline font-semibold">Try again</button>
    </div>
  );
  if (isLoading || !data) return <div className="card p-4 text-sm text-gray-500">Adding up {monthLabel(month)}…</div>;
  const r = data.revenue, c = data.crew_pay, jc = data.job_costs, oh = data.overhead, pr = data.profit;
  // The label comes from the data, so an old month's numbers never sit under a new month's name.
  const monthWords = monthLabel(data.month || month);
  const catWords = Object.entries(jc.by_category || {}).map(([k, v]) => `${k.replace(/_/g, ' ')} ${money(v as number)}`).join(' · ');

  const Line = ({ label, value, sub, sign, strong, tone }: { label: string; value: number; sub?: React.ReactNode; sign?: '-' | '='; strong?: boolean; tone?: string }) => (
    <div className={`flex items-baseline justify-between gap-3 ${strong ? 'pt-2 border-t border-gray-200' : ''}`}>
      <div className="min-w-0">
        <span className={`${strong ? 'font-bold text-gray-900' : 'text-gray-700'} text-sm`}>{sign ? `${sign} ` : ''}{label}</span>
        {sub && <span className="block text-[11px] text-gray-500">{sub}</span>}
      </div>
      <span className={`${strong ? 'text-xl font-bold' : 'text-sm font-semibold'} whitespace-nowrap ${tone || 'text-gray-900'}`}>{money(value)}</span>
    </div>
  );

  return (
    <div className={`card p-4 space-y-2 ${isPreviousData ? 'opacity-50' : ''}`} aria-busy={isPreviousData}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{monthWords}{data.partial ? ` · ${data.elapsed_days} of ${data.days} days` : ''} · finished work{isPreviousData ? ' · updating…' : ''}</p>
      <Line label="Earned" value={r.total}
        sub={[`${r.jobs.count} visit${r.jobs.count === 1 ? '' : 's'} ${money(r.jobs.total)}`, r.plans.total > 0 && `plans ${money(r.plans.total)}`, r.contracts.total > 0 && `contracts ${money(r.contracts.total)}`, r.expected && 'plans and contracts as due — billed at month end'].filter(Boolean).join(' · ')} />
      <Line label="Crew pay" value={c.employees} sign="-"
        sub={<>from the pay ledger{c.owed > 0 ? ` · ${money(c.owed)} still owed` : ''}</>} />
      <Line label="Job costs" value={jc.total} sign="-"
        sub={jc.total > 0 ? `${catWords}${jc.subcontractor > 0 ? ` · subs ${money(jc.subcontractor)}` : ''}` : 'none entered — add them on the jobs below'} />
      <Line label="Running the business" value={oh.total} sign="-"
        sub={<>{money(oh.cash)} spent · machines {money(oh.machines)}{data.partial ? ' so far' : ''}{Number(oh.marketing) > 0 ? ` · marketing ${money(oh.marketing)}` : ''}{Number(oh.meals) > 0 ? ` · meals ${money(oh.meals)}` : ''}{oh.unsorted?.count ? <span className="text-amber-700"> · {oh.unsorted.count} charge{oh.unsorted.count === 1 ? '' : 's'} unsorted</span> : null}</>} />
      <Line label="Left before you" value={pr.before_owner} sign="="
        tone={pr.before_owner >= 0 ? 'text-green-700' : 'text-red-600'}
        sub={<>{pr.pct != null ? `${pr.pct}% of what was earned` : ''}{data.real?.paid_pct != null ? ` · ${data.real.paid_pct}% of it actually paid so far` : ''}</>} />
      {/* The month's ledger holds his pay, not his hours: hours = his pay ÷ his own rate. */}
      <LaborerCompare before={pr.before_owner} rate={Number(data.laborer_rate) || 0} approx
        hours={Number(data.own_rate) > 0 ? (Number(c.owner) || 0) / Number(data.own_rate) : (Number(c.owner) > 0 ? null : 0)} />
      <Line label="You take home" value={750} sign="-"
        sub="You pay yourself $750 a month, not by the hour." />
      <Line label="What's left" value={Math.round((pr.before_owner - 750) * 100) / 100} sign="=" strong
        tone={(pr.before_owner - 750) >= 0 ? 'text-green-700' : 'text-red-600'}
        sub="The $750 is money you took home. It is not a cost. Left before you is what the business made." />
      {booked && (
        <div className={bookedStale ? 'opacity-50' : ''}>
          <Line label="Booked, not done yet" value={booked.charged}
            sub={`crew pay ${money(booked.pay)} · ${money(booked.margin)} before overhead — not in the numbers above`} />
        </div>
      )}
      {data.partial_failure && (
        <p className="text-xs bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2">
          Part of {monthWords} could not be read{Array.isArray(data.errors) && data.errors.length ? ` (${data.errors.join(', ')})` : ''} — these numbers are incomplete.
          <button type="button" onClick={() => refetch()} className="ml-2 underline font-semibold">Try again</button>
        </p>
      )}
      {(data.notes || []).filter((n: string) => !(data.partial_failure && n.startsWith('Could not read'))).map((n: string, i: number) => (
        <p key={i} className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">{n}</p>
      ))}
    </div>
  );
}

/** Sum the money fields over a group of rows. */
function sum(rows: any[]) {
  const t = { charged: 0, pay: 0, margin: 0 };
  for (const r of rows) { t.charged += r.charged; t.pay += r.total_pay; t.margin += r.margin; }
  return t;
}

function GroupRow({ title, sub, rows, open, onToggle }: { title: string; sub: string; rows: any[]; open: boolean; onToggle: () => void }) {
  const t = sum(rows);
  const pct = t.charged > 0 ? ` · ${Math.round((t.margin / t.charged) * 100)}%` : '';
  return (
    <button onClick={onToggle} className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
      <ChevronRight className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{title}</p>
        <p className="text-[11px] text-gray-500 truncate">{sub}</p>
      </div>
      <div className="text-right whitespace-nowrap">
        <p className={`text-sm font-bold ${t.margin >= 0 ? 'text-green-700' : 'text-red-600'}`}>{money(t.margin)}{pct}</p>
        <p className="text-[11px] text-gray-500">{money(t.charged)} charged</p>
      </div>
    </button>
  );
}

function DayView({ rows, onCost, readOnly }: { rows: any[]; onCost: (r: any) => void; readOnly: boolean }) {
  const [open, setOpen] = useState<string | null>(null);
  const days = useMemo(() => {
    const by = new Map<string, any[]>();
    for (const r of rows) { const k = String(r.date || '').slice(0, 10); (by.get(k) || by.set(k, []).get(k))!.push(r); }
    return Array.from(by.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [rows]);
  return (
    <div className="card divide-y divide-gray-100 p-0">
      {days.map(([day, list]) => {
        const routes = Array.from(new Set(list.map(r => r.route_name).filter(Boolean)));
        return (
          <div key={day}>
            <GroupRow title={dayLabel(day)} rows={list} open={open === day} onToggle={() => setOpen(o => (o === day ? null : day))}
              sub={`${list.length} stop${list.length === 1 ? '' : 's'}${routes.length ? ` · ${routes.join(', ')}` : ''}`} />
            {open === day && (
              <div className="px-3 pb-3 space-y-2">{list.map(r => <JobCard key={r.job_id} r={r} onCost={onCost} readOnly={readOnly} compact />)}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CustomerView({ rows, onCost, readOnly }: { rows: any[]; onCost: (r: any) => void; readOnly: boolean }) {
  const [open, setOpen] = useState<string | null>(null);
  const [worstFirst, setWorstFirst] = useState(false);
  const customers = useMemo(() => {
    const by = new Map<string, any[]>();
    for (const r of rows) { const k = r.customer_name || r.job_name || '—'; (by.get(k) || by.set(k, []).get(k))!.push(r); }
    const list = Array.from(by.entries());
    list.sort((a, b) => (worstFirst ? sum(a[1]).margin - sum(b[1]).margin : sum(b[1]).charged - sum(a[1]).charged));
    return list;
  }, [rows, worstFirst]);
  const losing = customers.filter(([, list]) => sum(list).margin < 0).length;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{losing ? `${losing} customer${losing === 1 ? '' : 's'} losing money in this period` : 'Nobody is losing you money in this period'}</p>
        <button onClick={() => setWorstFirst(w => !w)} className="text-xs font-semibold text-brand-700">{worstFirst ? 'Biggest first' : 'Worst first'}</button>
      </div>
      <div className="card divide-y divide-gray-100 p-0">
        {customers.map(([name, list]) => {
          const t = sum(list);
          return (
            <div key={name}>
              <GroupRow title={name} rows={list} open={open === name} onToggle={() => setOpen(o => (o === name ? null : name))}
                sub={`${list.length} visit${list.length === 1 ? '' : 's'} · ${money(t.margin / list.length)} a visit`} />
              {open === name && (
                <div className="px-3 pb-3 space-y-2">{list.map(r => <JobCard key={r.job_id} r={r} onCost={onCost} readOnly={readOnly} compact />)}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function JobCard({ r, onCost, readOnly, compact }: { r: any; onCost: (r: any) => void; readOnly: boolean; compact?: boolean }) {
  const costs = Math.round((r.charged - r.total_pay - r.margin) * 100) / 100;
  const notes: string[] = r.notes || [];
  // Gaps in amber, the rest (drive time, plan share, add-ons …) are facts and read as such.
  const gaps: string[] = Array.isArray(r.gaps) ? r.gaps : (isGapRow(r) ? notes : []);
  const info = notes.filter(n => !gaps.includes(n));
  return (
    <div className={`rounded-lg border border-gray-200 ${compact ? 'bg-gray-50' : 'card'} p-3`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{r.job_name}</p>
          <p className="text-[11px] text-gray-500 truncate">
            {dayLabel(String(r.date).slice(0, 10))}{r.route_name ? ` · ${r.route_name}` : ''}
            {' · '}<span className={`px-1 py-0.5 rounded ${STATUS_CHIP[r.status] || 'bg-gray-100 text-gray-600'}`}>{String(r.status).replace('_', ' ')}</span>
          </p>
        </div>
        <p className={`text-sm font-bold whitespace-nowrap ${r.margin >= 0 ? 'text-green-700' : 'text-red-600'}`}>{money(r.margin)}</p>
      </div>
      <div className="flex items-center justify-between gap-2 mt-1.5">
        <p className="text-[11px] text-gray-600">
          {money(r.charged)} − pay {money(r.total_pay)}
          {Math.abs(costs) > 0.005 && (
            <> − <button type="button" onClick={() => onCost(r)} className="underline decoration-dotted hover:text-gray-900" title="See, change or remove these costs">costs {money(costs)} ›</button></>
          )}
          {r.crew_names ? ` · ${r.crew_names}` : ''}
        </p>
        {!readOnly && (
          <button onClick={() => onCost(r)} className="flex-shrink-0 text-[11px] font-semibold text-brand-700 flex items-center gap-0.5">
            <Plus className="h-3 w-3" /> cost
          </button>
        )}
      </div>
      {gaps.length > 0 && (
        <p className="text-[11px] text-amber-700 mt-1 truncate" title={gaps.join('; ')}>{gaps.join(' · ')}</p>
      )}
      {info.length > 0 && (
        <p className="text-[11px] text-gray-500 mt-0.5 truncate" title={info.join('; ')}>{info.join(' · ')}</p>
      )}
    </div>
  );
}

type ShareJob = { id: number; customer_name: string | null; d: string | null; title: string | null; service_type?: string | null; status?: string; address?: string | null };
type JobExpense = {
  id: number; category: string; description: string | null; vendor: string | null; amount: number | string;
  incurred_on: string | null; bank_transaction_id?: number | null; receipt_id?: number | null; source?: string | null;
  // One cost over several jobs (services/costShare): the whole cost and every job's share.
  cost_group_id?: number | null;
  shared?: { total: number; shares: { id: number; job_id: number; amount: number; customer_name: string | null; d: string | null; title: string | null }[] } | null;
};

/**
 * A job's own costs: the ones already on it (to fix a fat-fingered 850 or take
 * one back out), and a form to add or edit one. Costs that came from the bank
 * or a receipt are changed from Bank expenses, so the bank line never ends up
 * marked as filed against a cost that no longer exists.
 */
function ExpenseModal({ job, readOnly, onClose }: { job: any; readOnly: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const jobId = job.job_id;
  const [editing, setEditing] = useState<JobExpense | null>(null);
  const [category, setCategory] = useState('material');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [vendor, setVendor] = useState('');
  const [incurredOn, setIncurredOn] = useState(String(job.date || '').slice(0, 10));

  const list = useQuery(['job-expenses', jobId], () => api.get(`/jobs/${jobId}/expenses`).then(r => (r.data?.expenses || []) as JobExpense[]));
  // Moving a cost to the job it was really for.
  const [moving, setMoving] = useState<JobExpense | null>(null);
  const [moveQ, setMoveQ] = useState('');
  const moveTerm = moveQ.trim();
  const targets = useQuery(['expense-move-targets', moveTerm, moving?.id],
    () => api.get('/expenses/jobs/search', { params: { q: moveTerm, date: String(moving?.incurred_on || job.date || '').slice(0, 10) } }).then(r => r.data.jobs as any[]),
    { enabled: !!moving && moveTerm.length >= 2, keepPreviousData: true });
  // A share of a split cost moves on its own, or the whole cost with `whole` (services/costShare).
  const move = useMutation((v: { id: number; to: number; whole?: boolean }) => api.post(`/jobs/${jobId}/expenses/${v.id}/move`, { job_id: v.to, whole: !!v.whole }), {
    onSuccess: (r: any) => { toast.success(r.data?.message || 'Moved'); setMoving(null); setMoveQ(''); invalidateMoney(qc); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not move that cost'); },
  });
  // Spreading a cost over jobs — a rental used on three jobs (owner, Oct 22 2026).
  const [sharing, setSharing] = useState<JobExpense | null>(null);
  // Taking a split cost off: this job's share (its money goes back over the other jobs) or the whole cost.
  const [removing, setRemoving] = useState<JobExpense | null>(null);
  const expenses = list.data || [];
  const subs = Number(job.subcontractor_cost) || 0;

  function resetForm() {
    setEditing(null); setCategory('material'); setAmount(''); setDescription(''); setVendor('');
    setIncurredOn(String(job.date || '').slice(0, 10));
  }
  function startEdit(e: JobExpense) {
    setEditing(e); setCategory(e.category); setAmount(String(Number(e.amount)));
    setDescription(e.description || ''); setVendor(e.vendor || ''); setIncurredOn(String(e.incurred_on || '').slice(0, 10));
  }

  const save = useMutation((payload: any) => (editing
    ? api.put(`/jobs/${jobId}/expenses/${editing.id}`, payload)
    : api.post(`/jobs/${jobId}/expenses`, payload)), {
    onSuccess: () => {
      invalidateMoney(qc);
      if (editing) { toast.success('Cost changed'); resetForm(); }
      else { toast.success('Cost added'); onClose(); }
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save the cost'); },
  });
  const remove = useMutation((v: { id: number; whole?: boolean }) => api.delete(`/jobs/${jobId}/expenses/${v.id}${v.whole ? '?whole=1' : ''}`), {
    onSuccess: (r: any, v) => { toast.success(r.data?.message || 'Cost removed'); setRemoving(null); if (editing?.id === v.id) resetForm(); invalidateMoney(qc); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not remove the cost'); },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!isFinite(amt) || amt <= 0) { toast.error('Enter an amount above 0'); return; }
    save.mutate({ category, amount: amt, description, vendor, incurred_on: incurredOn });
  }

  const categoryOptions = JOB_CATEGORIES.some(c => c.value === category) ? JOB_CATEGORIES
    : [...JOB_CATEGORIES, { value: category, label: LEGACY_CATEGORY_LABELS[category] || category, hint: '' }];
  const catLabel = (k: string) => JOB_CATEGORIES.find(c => c.value === k)?.label || LEGACY_CATEGORY_LABELS[k] || k;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h2 className="font-semibold text-gray-900">What did this job cost you?</h2>
            <p className="text-xs text-gray-500 truncate max-w-xs">{job.job_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-5 pt-4 space-y-1.5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">On this job</p>
          {list.isLoading ? <p className="text-sm text-gray-500">Loading…</p>
            : list.isError ? (
              <p className="text-sm text-red-600">Could not load the costs. <button type="button" onClick={() => list.refetch()} className="underline">Try again</button></p>
            ) : expenses.length === 0 && subs <= 0 ? <p className="text-sm text-gray-500">No costs yet.</p>
            : (
              <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                {expenses.map(x => {
                  const fromBank = !!(x.bank_transaction_id || x.receipt_id);
                  return (
                    <li key={x.id} className={`px-3 py-2 text-sm ${editing?.id === x.id ? 'bg-brand-50' : ''}`}>
                     <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 break-words">{x.description || catLabel(x.category)}{x.vendor ? <span className="text-gray-500"> · {x.vendor}</span> : null}</p>
                        <p className="text-[11px] text-gray-500">
                          {catLabel(x.category)}{x.incurred_on ? ` · ${dayLabel(String(x.incurred_on).slice(0, 10))}` : ''}
                          {fromBank ? ' · from the bank' : ''}
                          {x.shared ? ` · this job's share of ${money(x.shared.total)} over ${x.shared.shares.length} jobs` : ''}
                        </p>
                        {x.shared && (
                          <p className="text-[11px] text-gray-500">
                            {x.shared.shares.map(s => `${s.job_id === jobId ? 'this job' : (s.customer_name || `job #${s.job_id}`)}${s.d ? ` ${dayLabel(s.d)}` : ''} ${money(s.amount)}`).join(' · ')}
                          </p>
                        )}
                      </div>
                      <span className="font-semibold whitespace-nowrap">{money(Number(x.amount))}</span>
                      {!readOnly && (
                        <button type="button" onClick={() => { setMoving(moving?.id === x.id ? null : x); setMoveQ(''); setRemoving(null); }}
                          className="text-[11px] font-semibold text-brand-700 underline whitespace-nowrap">Wrong job?</button>
                      )}
                      {!readOnly && (
                        <button type="button" onClick={() => setSharing(x)}
                          className="text-[11px] font-semibold text-brand-700 underline whitespace-nowrap">{x.shared ? 'Change the split' : 'Split across jobs…'}</button>
                      )}
                      {!readOnly && !fromBank && !x.shared && (
                        <button type="button" title="Change it" onClick={() => startEdit(x)} className="p-1 text-gray-400 hover:text-gray-700"><Pencil className="h-3.5 w-3.5" /></button>
                      )}
                      {!readOnly && (!fromBank || x.shared) && (
                        <button type="button" title={x.shared ? 'Take it off' : 'Take it back out'} disabled={remove.isLoading}
                          onClick={() => {
                            if (x.shared) { setRemoving(removing?.id === x.id ? null : x); setMoving(null); return; }
                            if (window.confirm(`Remove ${money(Number(x.amount))}${x.description ? ` (${x.description})` : ''} from this job?`)) remove.mutate({ id: x.id });
                          }}
                          className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"><Trash2 className="h-3.5 w-3.5" /></button>
                      )}
                     </div>
                      {removing?.id === x.id && x.shared && (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                          <span className="text-gray-700">Take off:</span>
                          <button type="button" disabled={remove.isLoading} onClick={() => remove.mutate({ id: x.id })}
                            className="press px-2.5 py-1 rounded-lg border border-gray-300 text-gray-800 font-semibold disabled:opacity-50">This job's share — the other {x.shared.shares.length - 1} pick it up</button>
                          {!fromBank ? (
                            <button type="button" disabled={remove.isLoading} onClick={() => { if (window.confirm(`Remove the whole ${money(x.shared!.total)} from all ${x.shared!.shares.length} jobs?`)) remove.mutate({ id: x.id, whole: true }); }}
                              className="press px-2.5 py-1 rounded-lg border border-red-300 text-red-700 font-semibold disabled:opacity-50">The whole cost, off every job</button>
                          ) : <span className="text-gray-500">The whole charge is undone from Bank expenses below.</span>}
                        </div>
                      )}
                      {moving?.id === x.id && (
                        <div className="mt-2 space-y-1.5">
                          <input value={moveQ} onChange={e => setMoveQ(e.target.value)} autoFocus
                            placeholder="Who was it for? Customer or address"
                            className="input w-full py-1.5 text-sm" />
                          {moveTerm.length >= 2 && (targets.data?.length ? (
                            <ul className="max-h-48 overflow-y-auto space-y-1">
                              {targets.data.filter(j => j.id !== jobId).map(j => (
                                <li key={j.id}>
                                  <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-gray-200">
                                    <button type="button" disabled={move.isLoading} onClick={() => move.mutate({ id: x.id, to: j.id })}
                                      className="flex-1 min-w-0 text-left hover:text-brand-700 disabled:opacity-50">
                                      <span className="text-sm font-medium text-gray-900">{j.customer_name || `Job #${j.id}`}</span>
                                      <span className="text-xs text-gray-500"> · {dayLabel(j.date)}{j.service_type ? ` · ${j.service_type}` : ''}{j.title ? ` · ${j.title}` : ''}</span>
                                      {j.address && <span className="block text-[11px] text-gray-500">{String(j.address).split(',')[0]}</span>}
                                      {x.shared && <span className="block text-[11px] text-brand-700 font-semibold">Move this job's share here</span>}
                                    </button>
                                    {x.shared && (
                                      <button type="button" disabled={move.isLoading} onClick={() => move.mutate({ id: x.id, to: j.id, whole: true })}
                                        className="press text-[11px] px-2 py-1 rounded-lg border border-gray-300 text-gray-800 font-semibold whitespace-nowrap disabled:opacity-50">Whole cost</button>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : <p className="text-xs text-gray-500">{targets.isLoading ? 'Looking…' : `No job matches “${moveQ}”.`}</p>)}
                        </div>
                      )}
                    </li>
                  );
                })}
                {subs > 0 && (
                  <li className="px-3 py-2 flex items-center gap-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 truncate">Subcontractor{job.subcontractor_name ? ` · ${job.subcontractor_name}` : ''}</p>
                      <p className="text-[11px] text-gray-500">set on the job itself</p>
                    </div>
                    <span className="font-semibold whitespace-nowrap">{money(subs)}</span>
                  </li>
                )}
              </ul>
            )}
        </div>

        {readOnly ? (
          <div className="p-5 flex justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{editing ? 'Change this cost' : 'Add a cost'}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)} className="input w-full py-2 text-sm">
                  {categoryOptions.map(c => <option key={c.value} value={c.value} title={c.hint}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                <input type="number" min="0.01" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                  placeholder="0.00" autoFocus className="input w-full py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">What was it</label>
              <input value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Mulch, sod, pavers, weedkiller used on this lawn…" className="input w-full py-2 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Vendor</label>
                <input value={vendor} onChange={e => setVendor(e.target.value)} className="input w-full py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                <input type="date" value={incurredOn} onChange={e => setIncurredOn(e.target.value)} className="input w-full py-2 text-sm" />
              </div>
            </div>
            {!editing && (
              <p className="text-[11px] text-gray-500">Paid by card? Sort it from Bank expenses below instead — a cost typed here and the same bank charge would both count.</p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={editing ? resetForm : onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">{editing ? 'Cancel change' : 'Cancel'}</button>
              <button type="submit" disabled={save.isLoading}
                className="press bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-black text-sm font-medium px-4 py-2 rounded-xl">
                {save.isLoading ? 'Saving…' : editing ? 'Save change' : 'Add cost'}
              </button>
            </div>
          </form>
        )}
      </div>
      {sharing && <ShareCostModal jobId={jobId} job={job} expense={sharing} onClose={() => setSharing(null)} onDone={() => { setSharing(null); invalidateMoney(qc); }} />}
    </div>
  );
}

/**
 * One cost over several jobs (owner, Oct 22 2026: "split cost in between multiple jobs e.g. rental for multiple
 * jobs"). The other days of this job's run come first, then this customer's other recent work, then any job by
 * search. Equal shares, or the amounts he types (the last job takes what is left). A cost already split comes
 * back with its jobs ticked and its amounts filled in.
 */
function ShareCostModal({ jobId, job, expense, onClose, onDone }: { jobId: number; job: any; expense: JobExpense; onClose: () => void; onDone: () => void }) {
  const qc = useQueryClient();
  const total = expense.shared ? Number(expense.shared.total) : Number(expense.amount) || 0;
  const { data: cand, isLoading } = useQuery(['share-candidates', jobId], () => api.get(`/jobs/${jobId}/expenses/share-candidates`).then(r => r.data as { days: ShareJob[]; same_customer: ShareJob[] }));
  const [q, setQ] = useState('');
  const term = q.trim();
  const found = useQuery(['share-search', term, jobId],
    () => api.get('/expenses/jobs/search', { params: { q: term, date: String(expense.incurred_on || job.date || '').slice(0, 10) } })
      .then(r => ((r.data.jobs || []) as any[]).map(j => ({ id: j.id, customer_name: j.customer_name, d: j.date, title: j.title, service_type: j.service_type, status: j.status, address: j.address })) as ShareJob[]),
    { enabled: term.length >= 2, keepPreviousData: true });
  const self: ShareJob = { id: jobId, customer_name: job.customer_name || null, d: String(job.date || '').slice(0, 10) || null, title: job.job_name || null };
  const [picked, setPicked] = useState<ShareJob[]>(() => (expense.shared
    ? expense.shared.shares.map(s => ({ id: s.job_id, customer_name: s.customer_name, d: s.d, title: s.title }))
    : [self]));
  const [mode, setMode] = useState<'equal' | 'amounts'>(expense.shared ? 'amounts' : 'equal');
  const [amounts, setAmounts] = useState<Record<string, string>>(() => (expense.shared ? Object.fromEntries(expense.shared.shares.map(s => [String(s.job_id), String(s.amount)])) : {}));
  const has = (id: number) => picked.some(p => p.id === id);
  const toggle = (j: ShareJob) => setPicked(p => (has(j.id) ? p.filter(x => x.id !== j.id) : [...p, j]));
  const each = picked.length ? Math.round((total / picked.length) * 100) / 100 : 0;
  const typed = Math.round(picked.reduce((t, p) => t + (Number(amounts[String(p.id)]) || 0), 0) * 100) / 100;
  const save = useMutation(() => api.post(`/jobs/${jobId}/expenses/${expense.id}/share`, {
    job_ids: picked.map(p => p.id), mode,
    amounts: mode === 'amounts' ? Object.fromEntries(picked.map(p => [String(p.id), amounts[String(p.id)] === '' || amounts[String(p.id)] == null ? null : Number(amounts[String(p.id)])])) : undefined,
  }), {
    onSuccess: (r: any) => { toast.success(r.data?.message || 'Split'); invalidateMoney(qc); onDone(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not split that cost'); },
  });
  const unshare = useMutation(() => api.post(`/jobs/${jobId}/expenses/${expense.id}/unshare`), {
    onSuccess: (r: any) => { toast.success(r.data?.message || 'Back on this job'); invalidateMoney(qc); onDone(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not put that cost back together'); },
  });
  const row = (j: ShareJob, tag?: string) => (
    <button key={j.id} type="button" onClick={() => toggle(j)}
      className={`w-full text-left px-2.5 py-1.5 rounded-lg border ${has(j.id) ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:border-brand-400'}`}>
      <span className="text-sm font-medium text-gray-900">{has(j.id) ? '✓ ' : ''}{j.id === jobId ? 'This job' : (j.customer_name || `Job #${j.id}`)}</span>
      <span className="text-xs text-gray-500"> · {j.d ? dayLabel(j.d) : ''}{j.title ? ` · ${j.title}` : j.service_type ? ` · ${j.service_type}` : ''}{tag ? ` · ${tag}` : ''}</span>
      {j.address && <span className="block text-[11px] text-gray-500">{String(j.address).split(',')[0]}</span>}
    </button>
  );
  const known = new Set([jobId, ...(cand?.days || []).map(d => d.id), ...(cand?.same_customer || []).map(d => d.id)]);
  const extra = picked.filter(p => !known.has(p.id));
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h2 className="font-semibold text-gray-900">Split {money(total)} across jobs</h2>
            <p className="text-xs text-gray-500 truncate max-w-xs">{expense.description || expense.category}{expense.vendor ? ` · ${expense.vendor}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="space-y-1">
            {row(self)}
            {extra.map(j => row(j))}
          </div>
          {isLoading ? <p className="text-sm text-gray-500">Loading jobs…</p> : (
            <>
              {!!cand?.days?.length && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">The other days of this job</p>
                  <div className="space-y-1">{cand.days.map(j => row(j, j.status === 'complete' ? 'done' : j.status))}</div>
                </div>
              )}
              {!!cand?.same_customer?.length && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">More of this customer's work</p>
                  <div className="space-y-1">{cand.same_customer.map(j => row(j))}</div>
                </div>
              )}
            </>
          )}
          <div>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Another job? Customer or address" className="input w-full py-1.5 text-sm" />
            {term.length >= 2 && (found.data?.length
              ? <div className="space-y-1 mt-1">{found.data.filter(j => !known.has(j.id)).map(j => row(j))}</div>
              : <p className="text-xs text-gray-500 mt-1">{found.isLoading ? 'Looking…' : `No job matches “${q}”.`}</p>)}
          </div>
          <div className="rounded-xl border border-brand-200 bg-brand-50/60 px-3 py-2.5 space-y-2">
            <p className="text-xs font-semibold text-gray-700">{picked.length < 2 ? 'Pick at least two jobs' : `${picked.length} jobs share ${money(total)}`}</p>
            {picked.length >= 2 && (
              <>
                <div className="flex gap-3 text-xs text-gray-700">
                  <label className="flex items-center gap-1"><input type="radio" checked={mode === 'equal'} onChange={() => setMode('equal')} className="text-brand-600" /> Equal — {money(each)} each</label>
                  <label className="flex items-center gap-1"><input type="radio" checked={mode === 'amounts'} onChange={() => setMode('amounts')} className="text-brand-600" /> Amounts I type</label>
                </div>
                <ul className="space-y-1">
                  {picked.map((p, i) => (
                    <li key={p.id} className="flex items-center gap-2 text-xs text-gray-800">
                      <span className="flex-1 truncate">{p.id === jobId ? 'This job' : (p.customer_name || `Job #${p.id}`)}{p.d ? ` · ${dayLabel(p.d)}` : ''}{p.title ? ` · ${p.title}` : ''}</span>
                      {mode === 'amounts'
                        ? <span className="inline-flex items-center gap-1">$<input type="number" min={0} step="0.01" value={amounts[String(p.id)] ?? ''} placeholder={i === picked.length - 1 ? 'the rest' : ''}
                            onChange={e => setAmounts(a => ({ ...a, [String(p.id)]: e.target.value }))} className="input w-24 py-1 text-xs" /></span>
                        : <span className="font-semibold">{money(i === picked.length - 1 ? Math.round((total - each * (picked.length - 1)) * 100) / 100 : each)}</span>}
                    </li>
                  ))}
                </ul>
                {mode === 'amounts' && <p className="text-[11px] text-gray-500">Typed so far {money(typed)} of {money(total)} — leave the last one blank and it takes the rest.</p>}
              </>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2 pt-1">
            {expense.shared && (
              <button type="button" disabled={unshare.isLoading} onClick={() => unshare.mutate()} className="mr-auto text-xs text-gray-600 underline disabled:opacity-50">Put the whole {money(total)} back on this job</button>
            )}
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="button" disabled={save.isLoading || picked.length < 2} onClick={() => save.mutate()}
              className="press bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-black text-sm font-medium px-4 py-2 rounded-xl">
              {save.isLoading ? 'Saving…' : `Split ${money(total)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
